import {getTestProps} from '@benchling/js-test-utils';
import {Alert, Anchor, Button, Group, PinInput, Stack} from '@mantine/core';
import React from 'react';

import SignupStepHeader from '../SignupStepHeader';
import {
  SIGNUP_BUTTON_SIZE,
  SIGNUP_COMPACT_STACK_SPACING,
  SIGNUP_FORM_STACK_SPACING,
  SIGNUP_STEP_STACK_SPACING,
} from '../signupUi';

const CODE_LENGTH = 6;

interface VerifyStepProps {
  code: string;
  email: string;
  error: string | null;
  loading: boolean;
  onBack: () => void;
  onCodeChange: (value: string) => void;
  onResendCode: () => void;
  onSubmit: () => void;
}

export default function VerifyStep({
  code,
  email,
  error,
  loading,
  onBack,
  onCodeChange,
  onResendCode,
  onSubmit,
}: VerifyStepProps): JSX.Element {
  const canSubmit = code.length === CODE_LENGTH && !loading;

  return (
    <Stack
      id="model-hub-signup-verify-form"
      spacing={SIGNUP_STEP_STACK_SPACING}
      {...getTestProps({component: 'ModelHubSignup', element: 'verifyForm'})}
    >
      <SignupStepHeader
        subtitle={
          <>
            We sent a {CODE_LENGTH}-digit code to <b>{email}</b>. Enter it below to confirm it&apos;s you.
          </>
        }
        title="Check your inbox"
        onBack={onBack}
      />
      {error != null && (
        <Alert color="red" role="alert" title="Unable to verify">
          {error}
        </Alert>
      )}
      <Stack spacing={SIGNUP_FORM_STACK_SPACING}>
        <Group position="center">
          <PinInput
            autoFocus={true}
            length={CODE_LENGTH}
            oneTimeCode={true}
            size="lg"
            spacing="sm"
            type="number"
            value={code}
            onChange={onCodeChange}
            onComplete={onSubmit}
          />
        </Group>
        <Stack mt={SIGNUP_FORM_STACK_SPACING} spacing={SIGNUP_COMPACT_STACK_SPACING}>
          <Button
            disabled={!canSubmit}
            fullWidth={true}
            loading={loading}
            size={SIGNUP_BUTTON_SIZE}
            onClick={onSubmit}
          >
            Verify &amp; continue
          </Button>
          <Group position="center">
            <Anchor component="button" disabled={loading} size="sm" type="button" onClick={onResendCode}>
              Didn&apos;t get it? Resend code
            </Anchor>
          </Group>
        </Stack>
      </Stack>
    </Stack>
  );
}
